import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { InfiniteScroll } from "components/infinite-scroll/InfiniteScroll";
import { ICannedResponseTag } from "components/message-box/canned-responses";
import { KeyCodeEnum } from "constants/keyCode";
import { usePrevious } from "hooks/use-previous";
import { iterableNumericArray } from "utils/array";

import { CannedResponseTagItem } from "./CannedResponseTagItem";
import { CannedResponsesEmptyState } from "./CannedResponsesEmptyState";
import { CannedResponsesLoader } from "./CannedResponsesLoader";
import {
  changeElementsFocus,
  clearFocus,
  focusElementByIndex,
  getTagItemByIndex,
  getTagItemIndex,
  isFirstElementAvailable,
  isLastElementAvailable,
  isNextElementAvailable,
  isPreviousElementAvailable,
} from "./helpers";
import { tagListContainerStyles } from "./styles";

const TAG_HEIGHT = 32;
const TAG_PADDING = 8;
const MAX_TAGS_IN_CONTAINER = 16;
const ENABLE_INFINITE_SCROLL_OVER = 100; // 90% of licenses have up to 100 canned responses

interface IProps {
  isVisible: boolean;
  searchPhrase: string;
  tags: ICannedResponseTag[];
  onClose: () => void;
  onSearchResultsCountChange?: (count: number) => void;
  onSelect: (tag: string) => void;
}

export const CannedResponsesTagList: React.FC<IProps> = ({
  tags,
  isVisible,
  searchPhrase,
  onClose,
  onSearchResultsCountChange,
  onSelect,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const focusedIndex = useRef<number>(null);
  const [page, setPage] = useState<number>(1);
  const prevSearchPhrase = usePrevious(searchPhrase, "");

  const filteredTags = useMemo(
    () =>
      searchPhrase
        ? tags.filter(item => item.name.toLocaleLowerCase().startsWith(searchPhrase.toLocaleLowerCase()))
        : tags,
    [tags, searchPhrase]
  );

  /**
   * Enable scrolling only for total number of tags greater than `ENABLE_INFINITE_SCROLL_OVER`.
   */
  const pageSize = tags.length > ENABLE_INFINITE_SCROLL_OVER ? MAX_TAGS_IN_CONTAINER * 4 : tags.length;
  const loadMoreThreshold = (pageSize / 2) * TAG_HEIGHT;
  const maxTagsToShow = page * pageSize;
  const tagsToShow = maxTagsToShow > filteredTags.length ? filteredTags.length : maxTagsToShow;
  const hasMore = filteredTags.length > maxTagsToShow;
  const height =
    (tagsToShow >= MAX_TAGS_IN_CONTAINER ? TAG_HEIGHT * MAX_TAGS_IN_CONTAINER : TAG_HEIGHT * tagsToShow) + TAG_PADDING;

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const updateFocusedIndex = (index: number) => {
    // @ts-expect-error TS(2540): Cannot assign to 'current' because it is a read-on... Remove this comment to see the full error message
    focusedIndex.current = index;
  };

  // FIXME: Fix this the next time the file is edited.
  // @ts-expect-error TS(2345): Argument of type 'number | null' is not assignable... Remove this comment to see the full error message
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const getFocusedTagElement = () => getTagItemByIndex(listRef, focusedIndex.current);

  const resetTagList = useCallback(() => {
    clearFocus(listRef);
    focusElementByIndex(listRef, 0);
    updateFocusedIndex(0);
    setPage(1);
  }, []);

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleContainerRef = (ref: HTMLDivElement) => {
    // @ts-expect-error TS(2540): Cannot assign to 'current' because it is a read-on... Remove this comment to see the full error message
    containerRef.current = ref;
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleListRef = (ref: HTMLDivElement) => {
    // @ts-expect-error TS(2540): Cannot assign to 'current' because it is a read-on... Remove this comment to see the full error message
    listRef.current = ref;
  };

  const scrollAfterFocusChange = useCallback((direction: "top" | "bottom") => {
    const focusedElement = getFocusedTagElement();
    if (focusedElement) {
      const focusedOffsetTop = focusedElement.offsetTop;
      const focusedOffsetBottom = TAG_HEIGHT + focusedOffsetTop;
      // @ts-expect-error TS(2531): Object is possibly 'null'.
      const containerScrollTop = containerRef.current.scrollTop;
      // @ts-expect-error TS(2531): Object is possibly 'null'.
      const containerHeight = containerRef.current.offsetHeight;

      if (direction === "top") {
        const diff = containerScrollTop - focusedOffsetTop;
        if (diff > 0) {
          // @ts-expect-error TS(2531): Object is possibly 'null'.
          containerRef.current.scrollTop -= diff;
        }
      } else {
        const diff = focusedOffsetBottom - containerScrollTop - containerHeight;
        if (diff > 0) {
          // @ts-expect-error TS(2531): Object is possibly 'null'.
          containerRef.current.scrollTop += diff;
        }
      }
    }
  }, []);

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const scrollToBound = (direction: "top" | "bottom") => {
    if (direction === "top") {
      // @ts-expect-error TS(2531): Object is possibly 'null'.
      containerRef.current.scrollTop = 0;
    } else {
      // @ts-expect-error TS(2531): Object is possibly 'null'.
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  };

  const handleLoadMore = useCallback(() => {
    setPage(page + 1);
  }, [page]);

  const handleTagClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      event.preventDefault();
      event.stopPropagation();
      event.nativeEvent.stopImmediatePropagation();

      const tagIndex = getTagItemIndex(event.currentTarget);
      if (tagIndex !== null) {
        onSelect(filteredTags[tagIndex].name);
      }
    },
    [onSelect, filteredTags]
  );

  const handleTagMouseEnter = useCallback((event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (!event.currentTarget.classList.contains("focused")) {
      const previousFocused = getFocusedTagElement();
      if (previousFocused) {
        previousFocused.classList.remove("focused");
      }

      event.currentTarget.classList.add("focused");
      // @ts-expect-error TS(2540): Cannot assign to 'current' because it is a read-on... Remove this comment to see the full error message
      focusedIndex.current = getTagItemIndex(event.currentTarget);
    }
  }, []);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      const handledKeys = [
        KeyCodeEnum.Enter,
        KeyCodeEnum.ArrowRight,
        KeyCodeEnum.ArrowLeft,
        KeyCodeEnum.ArrowUp,
        KeyCodeEnum.ArrowDown,
      ];

      if (isVisible && handledKeys.includes(e.keyCode)) {
        // Handle keys only in case of existing tags
        if (filteredTags.length > 0) {
          e.preventDefault();
          if (e.keyCode === KeyCodeEnum.Enter || e.keyCode === KeyCodeEnum.ArrowRight) {
            // @ts-expect-error TS(2538): Type 'null' cannot be used as an index type.
            onSelect(filteredTags[focusedIndex.current].name);
          }

          if (e.keyCode === KeyCodeEnum.ArrowLeft) {
            onClose();
          }

          if (e.keyCode === KeyCodeEnum.ArrowUp) {
            const focusedElement = getFocusedTagElement();
            if (focusedElement) {
              // @ts-expect-error TS(2531): Object is possibly 'null'.
              const shouldFocusPrevious = focusedIndex.current > 0;
              const shouldFocusBottom = focusedIndex.current === 0 && filteredTags.length === tagsToShow;

              if (shouldFocusPrevious && isPreviousElementAvailable(focusedElement)) {
                const previousElement = focusedElement.previousElementSibling;
                // @ts-expect-error TS(2345): Argument of type 'Element | null' is not assignabl... Remove this comment to see the full error message
                changeElementsFocus(focusedElement, previousElement);
                // @ts-expect-error TS(2531): Object is possibly 'null'.
                updateFocusedIndex(focusedIndex.current - 1);
                scrollAfterFocusChange("top");
              } else if (shouldFocusBottom && isLastElementAvailable(listRef, focusedElement)) {
                // @ts-expect-error TS(2339): Property 'lastElementChild' does not exist on type... Remove this comment to see the full error message
                const { lastElementChild } = listRef.current;
                changeElementsFocus(focusedElement, lastElementChild);
                updateFocusedIndex(tagsToShow - 1);
                scrollToBound("bottom");
              }
            }
          }

          if (e.keyCode === KeyCodeEnum.ArrowDown) {
            const focusedElement = getFocusedTagElement();
            if (focusedElement) {
              // @ts-expect-error TS(2531): Object is possibly 'null'.
              const shouldFocusNext = focusedIndex.current < tagsToShow - 1;
              const shouldFocusTop = focusedIndex.current === filteredTags.length - 1;

              if (shouldFocusNext && isNextElementAvailable(focusedElement)) {
                const nextElement = focusedElement.nextElementSibling;
                // @ts-expect-error TS(2345): Argument of type 'Element | null' is not assignabl... Remove this comment to see the full error message
                changeElementsFocus(focusedElement, nextElement);
                // @ts-expect-error TS(2531): Object is possibly 'null'.
                updateFocusedIndex(focusedIndex.current + 1);
                scrollAfterFocusChange("bottom");
              } else if (shouldFocusTop && isFirstElementAvailable(listRef, focusedElement)) {
                // @ts-expect-error TS(2339): Property 'firstElementChild' does not exist on typ... Remove this comment to see the full error message
                const { firstElementChild } = listRef.current;
                changeElementsFocus(focusedElement, firstElementChild);
                updateFocusedIndex(0);
                scrollToBound("top");
              }
            }
          }
          // in case of empty tag list, just close the canned responses
        } else if (e.keyCode === KeyCodeEnum.Enter) {
          onClose();
        }
      }
    },
    [focusedIndex, isVisible, onClose, onSelect, filteredTags, tagsToShow, scrollAfterFocusChange]
  );

  /**
   * If search phrase changes we want to reset page back to first.
   * Focus is also changed back to the first one.
   */
  useEffect(() => {
    if (prevSearchPhrase !== searchPhrase) {
      resetTagList();
    }
  }, [prevSearchPhrase, resetTagList, searchPhrase]);

  /**
   * Reset focus back to first tag when tag list is brought back to visible.
   */
  useEffect(() => {
    if (isVisible && focusedIndex.current === null) {
      resetTagList();
    }
  }, [isVisible, focusedIndex, resetTagList]);

  /**
   * Autoselect when:
   * * search phrase narrows results to only 1
   * * search phrase is being typed in additive mode (prevents autoselecting when characters are removed)
   *
   * Autodeselect when:
   * * search phrase narrows tags to more than 1
   * * search phrase is being typed in remove mode
   */
  useEffect(() => {
    if (isVisible && filteredTags.length === 1 && prevSearchPhrase.length < searchPhrase.length) {
      onSelect(filteredTags[0].name);
    }

    if (!isVisible && filteredTags.length > 1 && prevSearchPhrase.length > searchPhrase.length) {
      // @ts-expect-error TS(2345): Argument of type 'null' is not assignable to param... Remove this comment to see the full error message
      onSelect(null);
    }
  }, [isVisible, filteredTags, onSelect, prevSearchPhrase, searchPhrase]);

  /**
   * KeyDown listeners
   */
  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);

    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown]);

  useEffect(() => {
    if (onSearchResultsCountChange) {
      onSearchResultsCountChange(filteredTags.length);
    }
  }, [filteredTags.length, onSearchResultsCountChange]);

  const numberedTags = iterableNumericArray(tagsToShow);

  if (tags.length === 0) {
    return <CannedResponsesEmptyState />;
  }

  if (numberedTags.length === 0) {
    return null;
  }

  return (
    <InfiniteScroll
      css={tagListContainerStyles(isVisible)}
      onContainerRef={handleContainerRef}
      height={height}
      hasMore={hasMore}
      isLoading={false}
      listRef={listRef}
      onLoadMore={handleLoadMore}
      threshold={loadMoreThreshold}
    >
      <div data-testid="canned-responses-shortcut-list" ref={handleListRef}>
        {numberedTags.map(index => (
          <CannedResponseTagItem
            key={filteredTags[index].name}
            count={filteredTags[index].values.length}
            index={index}
            name={filteredTags[index].name}
            onClick={handleTagClick}
            onMouseEnter={handleTagMouseEnter}
          />
        ))}
        {hasMore && <CannedResponsesLoader />}
      </div>
    </InfiniteScroll>
  );
};
